<script setup>
import { motion, AnimatePresence } from "motion-v";
import { ref, onMounted, onUnmounted, nextTick, watch, computed } from "vue";
import { useChat } from "@/composables/useSidebar";
import { useEmoji } from "@/composables/useEmoji";
import { useDevice } from "@/composables/useDevice";
import { useSocket } from "@/composables/useSocket";
import { useAuth } from "@/composables/useAuth";
import apiClient from "@/composables/common/axios";

const props = defineProps({
  variant: {
    type: String,
    default: "",
  },
  isOpen: {
    type: Boolean,
    default: undefined,
  },
});

const emits = defineEmits(["close"]);

// ===== Composables =====
const { isChatOpen: globalIsChatOpen, unreadMessageCount, closeChat: globalCloseChat } = useChat();
const { isLoggedIn, currentUser } = useAuth();
const { connect, on, emit: socketEmit, disconnect, off } = useSocket();

// isOpen prop이 제공되면 prop을 사용하고, 아니면 전역 상태 사용
const isChatOpen = computed(() => {
  return props.isOpen !== undefined ? props.isOpen : globalIsChatOpen.value;
});

const closeChat = () => {
  if (props.isOpen !== undefined) {
    emits("close");
  } else {
    globalCloseChat();
  }
};
const { emojiCategories } = useEmoji();
const { isMobile } = useDevice();

// 현재 선택된 카테고리
const selectedCategory = ref(emojiCategories[0].id);

// 아바타 경로 정규화 함수
const normalizeAvatar = (avatar) => {
  if (!avatar) return '01';

  // 이미 전체 URL인 경우 숫자만 추출
  if (avatar.includes('http') || avatar.includes('.png')) {
    const match = avatar.match(/default_avatar_(\d{2})/);
    return match ? match[1] : '01';
  }

  // 이미 2자리 숫자인 경우 그대로 반환
  if (/^\d{2}$/.test(avatar)) {
    return avatar;
  }

  // 1자리 숫자인 경우 0 패딩
  if (/^\d$/.test(avatar)) {
    return avatar.padStart(2, '0');
  }

  return '01';
};

// ===== 상수 =====
const BASE_HEIGHT = computed(() => (isMobile.value ? 38 : 46));
const MAX_HEIGHT = 100;

// ===== Refs =====
const textareaRef = ref(null);
const sendButtonRef = ref(null);
const chatBodyRef = ref(null);
const chatRef = ref(null);
const textareaHeight = ref(`${BASE_HEIGHT.value}px`);
const isMultiline = ref(false);
const isEmojiOpen = ref(false);
const isGifOpen = ref(false);
const isAtBottom = ref(true);
const isScrollable = ref(false);
const lastMessageCount = ref(0);

// Giphy 관련 Refs
const gifs = ref([]);
// Giphy API Key (개발용으로 발급 받은 Key, 추후 배포 시 변경 필요)
const apiKey = "qxiEb2d61xFQrwj7IE5uw6E32RY1tMDN";

// 실제 채팅 메시지 (더미 데이터 제거, Socket.io로 수신)
const messages = ref([]);
const onlineCount = ref(0);
const lastMessageId = ref(0);
let socketInstance = null;

// ===== 유틸리티 함수 =====
const formatMessageWithMentions = (text) => {
  if (!text) return "";

  // @로 시작하는 멘션을 찾는 정규식 (@username 형식, 한글 포함)
  const mentionRegex = /@([가-힣a-zA-Z0-9_]+)/g;

  return text.replace(mentionRegex, '<span class="chat__mention">@$1</span>');
};

// ===== Textarea 높이 관리 =====
const updateTextareaHeight = () => {
  if (!textareaRef.value) return;

  nextTick(() => {
    if (!textareaRef.value) return;

    textareaRef.value.style.height = "1px";
    const scrollHeight = textareaRef.value.scrollHeight;
    const calculatedHeight = Math.max(
      BASE_HEIGHT.value,
      Math.min(scrollHeight, MAX_HEIGHT)
    );

    isMultiline.value = calculatedHeight > BASE_HEIGHT.value;

    const newHeightValue = `${calculatedHeight}px`;
    textareaHeight.value = newHeightValue;
    textareaRef.value.style.height = textareaHeight.value;

    if (calculatedHeight >= MAX_HEIGHT) {
      textareaRef.value.style.overflowY = "auto";
    } else {
      textareaRef.value.style.overflowY = "hidden";
    }
  });
};

// ===== 이벤트 핸들러 =====
const handleInput = () => {
  updateTextareaHeight();
};

const handleSend = async () => {
  if (!textareaRef.value) return;

  const message = textareaRef.value.value.trim();
  if (!message) return;

  // 로그인 체크
  if (!isLoggedIn.value) {
    console.warn('[Chat] Login required to send messages');
    return;
  }

  // 메시지 전송 (Socket.io 우선, 폴백으로 API)
  if (socketInstance) {
    socketEmit('chat:send', { message });
  } else {
    // API 폴백
    try {
      const response = await apiClient.post('/api/chat.php?action=send', { message });
      if (response.data.success && response.data.message) {
        const msg = response.data.message;
        messages.value.push({
          id: msg.id,
          text: msg.message,
          type: "outgoing",
          author: currentUser.value?.nickname || null,
          avatar: currentUser.value?.avatar || '01',
          time: msg.time
        });
      }
    } catch (error) {
      console.error('[Chat] Send error:', error);
    }
  }

  // 입력창 초기화
  textareaRef.value.value = "";
  textareaHeight.value = `${BASE_HEIGHT.value}px`;
  isMultiline.value = false;

  if (textareaRef.value) {
    textareaRef.value.style.height = "48px";
    textareaRef.value.style.overflowY = "hidden";
  }

  // 스크롤 유지
  nextTick(() => {
    scrollToBottom();
  });
};

const handleKeydown = (event) => {
  if (event.key === "Enter") {
    if (event.shiftKey) {
      setTimeout(() => {
        updateTextareaHeight();
      }, 0);
      return;
    }

    event.preventDefault();
    handleSend();
  }
};

const handleKeyup = (event) => {
  if (event.key === "Backspace" || event.key === "Delete") {
    updateTextareaHeight();
  }
};

const handleEmojiClick = () => {
  isEmojiOpen.value = !isEmojiOpen.value;
  // 이모지가 열리면 GIF 닫기
  if (isEmojiOpen.value) {
    isGifOpen.value = false;
  }
};

const handleGifClick = () => {
  isGifOpen.value = !isGifOpen.value;
  // GIF가 열리면 이모지 닫기
  if (isGifOpen.value) {
    isEmojiOpen.value = false;
  }
};

// 현재 선택된 카테고리의 이모지 목록
const currentEmojis = computed(() => {
  const category = emojiCategories.find(
    (cat) => cat.id === selectedCategory.value
  );
  return category ? category.emojis : [];
});

// 이모지 선택 핸들러
const onEmojiSelect = (emoji) => {
  if (!textareaRef.value) return;

  const emojiChar =
    typeof emoji === "string" ? emoji : emoji.i || emoji.emoji || emoji;

  // 현재 커서 위치에 이모지 삽입
  const start = textareaRef.value.selectionStart;
  const end = textareaRef.value.selectionEnd;
  const text = textareaRef.value.value;
  const before = text.substring(0, start);
  const after = text.substring(end, text.length);

  textareaRef.value.value = before + emojiChar + after;

  // 커서 위치를 이모지 뒤로 이동
  const newPosition = start + emojiChar.length;

  // 포커스가 유지된 상태에서 커서 위치만 재설정
  textareaRef.value.setSelectionRange(newPosition, newPosition);

  // 높이 업데이트
  updateTextareaHeight();
};

// 카테고리 변경 핸들러
const selectCategory = (categoryId) => {
  selectedCategory.value = categoryId;
};

// Giphy 트렌딩 데이터 가져오기
const fetchGifs = async () => {
  try {
    // limit=30 설정
    const response = await fetch(
      `https://api.giphy.com/v1/gifs/trending?api_key=${apiKey}&limit=30&rating=g`
    );
    const { data } = await response.json();
    gifs.value = data;
  } catch (error) {
    console.error("Giphy 데이터를 불러오는데 실패했습니다:", error);
  }
};

// GIF 선택 핸들러
const onGifSelect = (imageUrl, event) => {
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }

  // 이미지를 메시지로 바로 추가
  const wasAtBottom = isAtBottom.value;

  messages.value.push({
    id: Date.now(),
    text: "",
    image: imageUrl,
    type: "outgoing",
    author: null,
  });

  // 바닥에 있었다면 스크롤 유지
  if (wasAtBottom) {
    nextTick(() => {
      scrollToBottom();
    });
  }

  // GIF 피커 닫기
  isGifOpen.value = false;
};

// ===== 스크롤 관리 =====
const checkScrollable = () => {
  if (!chatBodyRef.value) return false;

  const messageList = chatBodyRef.value.querySelector(".chat__message-list");
  if (!messageList) return false;

  // chat__body의 높이와 chat__message-list의 높이 비교
  const bodyHeight = chatBodyRef.value.clientHeight;
  const listHeight = messageList.scrollHeight;

  return listHeight > bodyHeight;
};

const checkIfAtBottom = () => {
  if (!chatBodyRef.value) return;

  // 스크롤 가능 여부 확인
  const scrollable = checkScrollable();
  isScrollable.value = scrollable;

  // 스크롤이 불가능하면 바닥으로 간주하고 카운트 초기화
  if (!scrollable) {
    isAtBottom.value = true;
    unreadMessageCount.value = 0;
    lastMessageCount.value = messages.value.length;
    return;
  }

  const { scrollTop, scrollHeight, clientHeight } = chatBodyRef.value;
  const threshold = 10; // 10px 여유를 두고 바닥으로 간주
  const atBottom = scrollHeight - scrollTop - clientHeight <= threshold;

  isAtBottom.value = atBottom;

  // 바닥에 도달하면 읽지 않은 메시지 개수 초기화
  if (atBottom && unreadMessageCount.value > 0) {
    unreadMessageCount.value = 0;
    lastMessageCount.value = messages.value.length;
  }
};

const scrollToBottom = () => {
  nextTick(() => {
    if (chatBodyRef.value) {
      chatBodyRef.value.scrollTo({
        top: chatBodyRef.value.scrollHeight,
        behavior: "instant", // 'smooth'보다 'instant'가 더 빠릿한 채팅 UI에 적합합니다.
      });
      // 스크롤 후 상태 업데이트
      nextTick(() => {
        checkIfAtBottom();
      });
    }
  });
};

const handleScroll = () => {
  checkIfAtBottom();
};

const handleScrollToBottomClick = (event) => {
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }
  scrollToBottom();
  unreadMessageCount.value = 0;
};

// ===== Computed =====
const showScrollButton = computed(() => {
  return isScrollable.value && !isAtBottom.value;
});

// ===== Watchers =====
// 타이머 ID를 저장할 변수
let closeTimer = null;

// 채팅창이 열릴 때 바닥으로 스크롤
watch(
  isChatOpen,
  async (isOpen) => {
    if (closeTimer) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }

    if (isOpen) {
      await nextTick();

      if (chatBodyRef.value) {
        chatBodyRef.value.scrollTop = chatBodyRef.value.scrollHeight;
      }

      setTimeout(() => {
        if (chatBodyRef.value) {
          chatBodyRef.value.scrollTop = chatBodyRef.value.scrollHeight;
        }
      }, 100);
    } else {
      closeTimer = setTimeout(() => {
        isEmojiOpen.value = false;
        isGifOpen.value = false;
      }, 500);
    }
  }
);

watch(
  () => messages.value.length,
  (newLength, oldLength) => {
    const addedMessages = newLength - oldLength;

    if (addedMessages > 0) {
      const wasAtBottom = isAtBottom.value;

      nextTick(() => {
        if (!chatBodyRef.value) return;

        const scrollable = checkScrollable();
        isScrollable.value = scrollable;

        if (!scrollable) {
          isAtBottom.value = true;
          unreadMessageCount.value = 0;
          lastMessageCount.value = newLength;
          return;
        }

        if (wasAtBottom) {
          nextTick(() => {
            scrollToBottom();
            unreadMessageCount.value = 0;
            lastMessageCount.value = newLength;
          });
        } else {
          unreadMessageCount.value += addedMessages;
          checkIfAtBottom();
        }
      });
    }
  }
);

// GIF 탭이 열릴 때만 API 호출 (최초 1회)
watch(isGifOpen, (newVal) => {
  if (newVal && gifs.value.length === 0) {
    fetchGifs();
  }
});

// ===== 외부 클릭 핸들러 (isMobile이거나 variant가 'sub'일 때만 동작) =====
const handleOutsideClick = (event) => {
  // 조건 확인: isMobile이거나 variant가 'sub'일 때만
  if (!isMobile.value && props.variant !== 'sub') return;
  
  // 채팅이 열려있지 않으면 무시
  if (!isChatOpen.value) return;
  
  // Header 영역 클릭 시 제외
  const headerElement = event.target.closest('.header, .header__gnb, .header__mobile-gnb');
  if (headerElement) {
    return;
  }
  
  // chat 영역 내부 클릭은 무시
  if (chatRef.value) {
    const element = chatRef.value;
    // DOM 요소인지 확인하고 contains 메서드 사용
    if (element && typeof element.contains === 'function' && element.contains(event.target)) {
      return;
    }
  }
  
  // 외부 클릭이면 닫기
  closeChat();
};

// ===== Watchers =====
// isMobile 변경 시 textarea 높이 업데이트
watch(isMobile, () => {
  if (textareaRef.value && !isMultiline.value) {
    textareaHeight.value = `${BASE_HEIGHT.value}px`;
    textareaRef.value.style.height = textareaHeight.value;
  }
});

// isChatOpen이 변경될 때 이벤트 리스너 추가/제거
watch(
  () => isChatOpen.value,
  (newValue) => {
    if (process.client) {
      if (newValue && (isMobile.value || props.variant === 'sub')) {
        // 채팅이 열리고 조건이 맞으면 이벤트 리스너 추가
        window.addEventListener('click', handleOutsideClick);
      } else {
        // 채팅이 닫히면 이벤트 리스너 제거
        window.removeEventListener('click', handleOutsideClick);
      }
    }
  },
  { immediate: true }
);

// ===== Lifecycle Hooks =====
onMounted(() => {
  if (textareaRef.value) {
    textareaHeight.value = `${BASE_HEIGHT.value}px`;
    textareaRef.value.style.height = textareaHeight.value;
  }

  if (chatBodyRef.value) {
    chatBodyRef.value.addEventListener("scroll", handleScroll);
    lastMessageCount.value = messages.value.length;

    nextTick(() => {
      checkIfAtBottom();
      scrollToBottom();
    });
  }

  // Socket.io 연결 및 채팅 이벤트 구독
  if (currentUser.value) {
    socketInstance = connect(
      currentUser.value.id,
      currentUser.value.nickname,
      currentUser.value.avatar || '01'
    );
  } else {
    // 비로그인 사용자도 채팅 조회 가능
    socketInstance = connect(
      null,
      'Guest_' + Math.random().toString(36).substring(7),
      '01'
    );
  }

  // 채팅 히스토리 수신
  on('chat:history', (serverMessages) => {
    // // console.log('[Chat] Received history:', serverMessages.length, 'messages');
    messages.value = serverMessages.map(msg => ({
      id: msg.id,
      text: msg.message,
      type: msg.user_id === currentUser.value?.id ? 'outgoing' : 'incoming',
      author: msg.nickname,
      avatar: msg.avatar || '01',
      time: msg.time
    }));

    if (serverMessages.length > 0) {
      lastMessageId.value = Math.max(...serverMessages.map(m => m.id));
    }

    nextTick(() => {
      scrollToBottom();
    });
  });

  // 새 메시지 수신
  on('chat:message', (msg) => {
    // // console.log('[Chat] New message:', msg.nickname, msg.message);

    if (msg.id > lastMessageId.value) {
      messages.value.push({
        id: msg.id,
        text: msg.message,
        type: msg.user_id === currentUser.value?.id ? 'outgoing' : 'incoming',
        author: msg.nickname,
        avatar: msg.avatar || '01',
        time: msg.time
      });
      lastMessageId.value = msg.id;

      if (!isChatOpen.value) {
        unreadMessageCount.value++;
      } else {
        nextTick(() => {
          scrollToBottom();
        });
      }
    }
  });

  // 메시지 전송 확인
  on('chat:sent', (msg) => {
    // // console.log('[Chat] Message sent:', msg.id);
    // 내가 보낸 메시지는 chat:message로 다시 받으므로 여기서 추가 처리 불필요
  });

  // 온라인 사용자 수 업데이트
  on('chat:online', (data) => {
    // // console.log('[Chat] Online count:', data.count);
    onlineCount.value = data.count;
  });

  // 채팅 오류
  on('chat:error', (data) => {
    console.error('[Chat] Error:', data.message);
  });
});

onUnmounted(() => {
  if (closeTimer) clearTimeout(closeTimer);
  if (chatBodyRef.value) {
    chatBodyRef.value.removeEventListener("scroll", handleScroll);
  }
  // 외부 클릭 이벤트 리스너 제거
  if (process.client) {
    window.removeEventListener('click', handleOutsideClick);
  }

  // Socket.io 이벤트 리스너 제거
  off('chat:history');
  off('chat:message');
  off('chat:sent');
  off('chat:online');
  off('chat:error');
});
</script>

<template>
  <motion.aside
    :class="['chat', { 'chat--sub': variant === 'sub', 'chat--open': isChatOpen }]"
    :initial="{ x: '100%' }"
    :animate="
      isChatOpen
        ? {
            x: 0,
          }
        : {
            x: '100%',
          }
    "
    :transition="{
      type: 'spring',
      stiffness: 320,
      damping: 25,
      mass: 0.8,
    }"
  >
    <div ref="chatRef" class="chat__inner">
      <div class="chat__header">
        <h2 class="chat__title">채팅 <span v-if="onlineCount > 0">({{ onlineCount }}명 접속중)</span></h2>
        <button type="button" class="ico-close" @click="closeChat">
          <span class="blind">닫기</span>
        </button>
      </div>
      <div ref="chatBodyRef" class="chat__body">
        <div class="chat__message-list">
          <!-- 메시지가 없을 때 -->
          <div v-if="messages.length === 0" class="chat__empty">
            <p>아직 채팅 메시지가 없습니다.</p>
          </div>

          <!-- 메시지 목록 -->
          <div
            v-for="message in messages"
            :key="message.id"
            :class="[
              'chat__message',
              message.type === 'outgoing'
                ? 'chat__message--outgoing'
                : 'chat__message--incoming',
            ]"
          >
            <template v-if="message.type === 'incoming'">
              <div class="chat__message-thumb">
                <img
                  :src="`/images/default_avatar_${normalizeAvatar(message.avatar)}.png`"
                  :alt="`${message.author} 프로필`"
                  @error="$event.target.src='/images/default_avatar_01.png'"
                />
              </div>
              <div class="chat__message-content">
                <span class="chat__message-author">{{ message.author }}</span>
                <p
                  class="chat__message-text"
                  v-html="formatMessageWithMentions(message.text)"
                ></p>
              </div>
            </template>
            <template v-else>
              <p
                v-if="message.text || message.image"
                class="chat__message-text"
              >
                <span
                  v-if="message.text"
                  v-html="formatMessageWithMentions(message.text)"
                ></span>
                <img
                  v-if="message.image"
                  :src="message.image"
                  alt="GIF"
                  class="chat__message-image"
                />
              </p>
            </template>
          </div>
        </div>
      </div>
      <div class="chat__footer">
        <button
          v-if="showScrollButton"
          type="button"
          class="chat__action-btn ico-after ico-chevron-down"
          @click.stop="handleScrollToBottomClick"
        >
          <span class="blind">맨 아래로 이동</span>
          <span v-if="unreadMessageCount > 0"
            >{{ unreadMessageCount }}개의 새 메시지</span
          >
        </button>

        <div class="chat__footer-inner" :class="{ 'chat__footer-inner--actions-open': isEmojiOpen || isGifOpen }">
          <div
            class="chat__input-group"
            :class="{ 'chat__input-group--column': isMultiline }"
          >
            <textarea
              ref="textareaRef"
              class="textarea"
              placeholder="메세지를 입력하세요."
              :style="{
                height: textareaHeight,
                resize: 'none',
                overflowY: 'hidden',
              }"
              @input="handleInput"
              @keydown="handleKeydown"
              @keyup="handleKeyup"
            ></textarea>
            <div class="chat__input-controls">
              <button
                type="button"
                class="chat__input-btn ico-emoji"
                :class="{ active: isEmojiOpen }"
                @click="handleEmojiClick"
              >
                <span class="blind">이모지</span>
              </button>
              <button
                type="button"
                class="chat__input-btn ico-gif"
                :class="{ active: isGifOpen }"
                @click="handleGifClick"
              >
                <span class="blind">GIF</span>
              </button>
            </div>
          </div>
          <button
            ref="sendButtonRef"
            type="button"
            class="chat__btn-send btn-primary ico-before ico-send"
            @click="handleSend"
          >
            <span class="blind">전송</span>
          </button>
        </div>

        <!-- 래퍼 추가 -->
        <div class="chat__message-actions-wrapper">
          <AnimatePresence>
            <motion.div
              v-if="isEmojiOpen"
              class="chat__message-actions chat__message-actions--emoji"
              :style="{
                position: isGifOpen ? 'absolute' : 'relative',
                width: '100%',
                bottom: 0,
              }"
              :initial="{ height: 0, opacity: 0, y: 20 }"
              :animate="{ height: '14.4rem', opacity: 1, y: 0 }"
              :exit="{ height: 0, opacity: 0, y: 20 }"
              :transition="{
                type: 'spring',
                stiffness: 300,
                damping: 30,
                mass: 0.8,
              }"
            >
              <!-- 카테고리 버튼 -->
              <div class="emoji-picker__categories">
                <button
                  v-for="category in emojiCategories"
                  :key="category.id"
                  type="button"
                  class="emoji-picker__category-btn"
                  :class="{ active: selectedCategory === category.id }"
                  @mousedown.prevent="selectCategory(category.id)"
                  :title="category.name"
                >
                  {{ category.icon }}
                </button>
              </div>

              <!-- 이모지 그리드 -->
              <div class="emoji-picker__grid">
                <button
                  v-for="(emoji, index) in currentEmojis"
                  :key="`${selectedCategory}-${index}`"
                  type="button"
                  class="emoji-picker__emoji-btn"
                  @mousedown.prevent="onEmojiSelect(emoji)"
                >
                  {{ emoji }}
                </button>
              </div>
            </motion.div>
          </AnimatePresence>

          <AnimatePresence>
            <motion.div
              v-if="isGifOpen"
              class="chat__message-actions chat__message-actions--gif"
              :style="{
                position: isEmojiOpen ? 'absolute' : 'relative',
                width: '100%',
                bottom: 0,
              }"
              :initial="{ height: 0, opacity: 0, y: 20 }"
              :animate="{ height: '14.4rem', opacity: 1, y: 0 }"
              :exit="{ height: 0, opacity: 0, y: 20 }"
              :transition="{
                type: 'spring',
                stiffness: 300,
                damping: 30,
                mass: 0.8,
              }"
            >
              <div class="gif-picker__grid">
                <div
                  class="gif-picker__grid-item"
                  v-for="gif in gifs"
                  :key="gif.id"
                  role="button"
                  @mousedown.prevent="
                    onGifSelect(gif.images.fixed_height.url, $event)
                  "
                >
                  <img
                    :src="gif.images.fixed_height_small.url"
                    :alt="gif.title"
                  />
                </div>
              </div>
              <div class="gif-picker__attribution">
                <img src="@/assets/images/giphy.webp" alt="Powered by GIPHY" />
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  </motion.aside>
</template>

<style scoped></style>
